import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  type Firestore,
  type Timestamp,
} from 'firebase/firestore'
import { firestoreDb } from '../firebase'
import { queuePendingExpenseIntake } from './queueService'
import type { PendingExpenseIntakePayload } from './helperOutboxDb'
import type { HelperAssignedItem } from './helperAssignedItemsService'
import { normalizeBudgetCategory, type BudgetCategory } from '../../types/budget'

export type PendingHelperExpenseStatus = 'PENDING' | 'APPROVED' | 'REJECTED'

export type PendingHelperExpense = {
  id: string
  workspaceId: string
  checklistItemId: string
  taskId: string | null
  amountMinor: number
  currencyCode: string
  category: BudgetCategory
  receiptUrl: string
  receiptStoragePath: string
  note?: string | null
  actorId: string
  actorName: string
  status: PendingHelperExpenseStatus
  createdAt: Timestamp | null
  reviewedBy?: string | null
  reviewedAt?: Timestamp | null
  expenseId?: string | null
}

const normalizeAmountMinor = (value: unknown): number => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || !Number.isInteger(numeric) || numeric < 0) {
    throw new Error('Expense amount must be integer minor units.')
  }
  return numeric
}

export const parseExpenseAmountToMinor = (value: string): number => {
  const normalized = value.trim().replace(/,/g, '')
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
    throw new Error('Enter an amount with up to 2 decimals.')
  }
  return Math.round(Number(normalized) * 100)
}

const toPendingHelperExpense = (id: string, data: Record<string, unknown>): PendingHelperExpense => ({
  id,
  workspaceId: String(data.workspaceId ?? ''),
  checklistItemId: String(data.checklistItemId ?? ''),
  taskId: typeof data.taskId === 'string' ? data.taskId : null,
  amountMinor: Number(data.amountMinor ?? 0),
  currencyCode: String(data.currencyCode ?? 'USD').toUpperCase(),
  category: normalizeBudgetCategory(data.category),
  receiptUrl: String(data.receiptUrl ?? ''),
  receiptStoragePath: String(data.receiptStoragePath ?? ''),
  note: typeof data.note === 'string' ? data.note : null,
  actorId: String(data.actorId ?? ''),
  actorName: String(data.actorName ?? 'Helper'),
  status: data.status === 'APPROVED' || data.status === 'REJECTED' ? data.status : 'PENDING',
  createdAt: (data.createdAt as Timestamp | null | undefined) ?? null,
  reviewedBy: typeof data.reviewedBy === 'string' ? data.reviewedBy : null,
  reviewedAt: (data.reviewedAt as Timestamp | null | undefined) ?? null,
  expenseId: typeof data.expenseId === 'string' ? data.expenseId : null,
})

export const buildPendingExpenseIntakePayload = (input: {
  item: HelperAssignedItem
  actor: { id: string; name: string }
  fileName: string
  contentType: string
  amountMinor: number
  currencyCode: string
  note?: string | null
  category?: string | null
}): Omit<PendingExpenseIntakePayload, 'blobKey'> => ({
  workspaceId: input.item.workspaceId,
  checklistItemId: input.item.id,
  taskId: input.item.parentContainerId || null,
  fileName: input.fileName,
  contentType: input.contentType,
  amountMinor: normalizeAmountMinor(input.amountMinor),
  currencyCode: input.currencyCode.trim().toUpperCase() || 'USD',
  category: input.category ?? null,
  note: input.note?.trim() || null,
  actorId: input.actor.id,
  actorName: input.actor.name,
  assignedAt: input.item.updatedAtIso ?? null,
})

export const submitPendingExpense = async (input: {
  item: HelperAssignedItem
  actor: { id: string; name: string }
  file: File
  amountMinor: number
  currencyCode: string
  note?: string | null
  category?: string | null
}) => {
  const payload = buildPendingExpenseIntakePayload({
    item: input.item,
    actor: input.actor,
    fileName: input.file.name,
    contentType: input.file.type || 'application/octet-stream',
    amountMinor: input.amountMinor,
    currencyCode: input.currencyCode,
    note: input.note,
    category: input.category,
  })
  return queuePendingExpenseIntake(payload, input.file)
}

export const subscribePendingHelperExpenses = (
  workspaceId: string,
  callback: (entries: PendingHelperExpense[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    callback([])
    return () => {}
  }
  return onSnapshot(
    query(
      collection(db, 'workspaces', workspaceId, 'pending_expenses'),
      where('status', '==', 'PENDING'),
      orderBy('createdAt', 'desc'),
    ),
    (snapshot) => callback(snapshot.docs.map((snap) => toPendingHelperExpense(snap.id, snap.data() as Record<string, unknown>))),
    (error) => onError?.(error),
  )
}
